import { QRCode } from "antd";
import React, { useState, useCallback } from "react";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import MesAppareils from "./MesAppareils";

const brand = {
  primary: "#242166",
  accent: "#a957f7",
  soft: "#f4f7fb",
  border: "#e5e7eb",
  text: "#0f172a",
};

const navLinkStyle = (isActive) => ({
  border: `1px solid ${brand.border}`,
  color: isActive ? "#fff" : brand.primary,
  background: isActive
    ? `linear-gradient(135deg, ${brand.primary}, ${brand.accent})`
    : "#fff",
  boxShadow: isActive
    ? "0 10px 24px rgba(36, 33, 102, 0.18)"
    : "0 6px 16px rgba(15, 23, 42, 0.08)",
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "10px 16px",
  marginRight: "12px",
});

const cardStyle = {
  border: `1px solid ${brand.border}`,
  boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
  background: "linear-gradient(180deg, #ffffff 0%, #f8faff 100%)",
};

const cardHeaderStyle = {
  background: "transparent",
  color: brand.primary,
  fontWeight: 700,
  borderBottom: `1px solid ${brand.border}`,
};

const primaryButtonStyle = {
  background: `linear-gradient(135deg, ${brand.primary}, ${brand.accent})`,
  borderColor: brand.primary,
  boxShadow: "0 10px 20px rgba(36, 33, 102, 0.22)",
};

const ghostButtonStyle = {
  borderColor: brand.border,
  color: brand.primary,
  boxShadow: "0 6px 16px rgba(15, 23, 42, 0.06)",
};

const ProfileSecurity = () => {
  const [qrCodeImage, setQrCodeImage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [isOtpEnabled, setIsOtpEnabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const persistUser = (nextUser) => {
    try {
      sessionStorage.setItem("user", JSON.stringify(nextUser));
    } catch {
      // ignore storage errors
    }
  };

  const getSessionIdentifier = () =>
    LOGGED_USER?.userName || LOGGED_USER?.username || LOGGED_USER?.email;

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");

  // --- NOUVEAUX ÉTATS POUR LA VISIBILITÉ DES MOTS DE PASSE ---
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // -----------------------------------------------------------

  // --- Gestion du formulaire de changement de mot de passe ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPasswordError("");
    setSuccessMessage("");
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    setPasswordError("");
    setSuccessMessage("");

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Veuillez remplir tous les champs de mot de passe.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Le nouveau mot de passe et la confirmation ne correspondent pas.");
      return;
    }

    if (newPassword.length < 8) {
        setPasswordError("Le nouveau mot de passe doit contenir au moins 8 caractères.");
        return;
    }

    setIsChangingPassword(true);

    try {
      await axiosInstance.post("/api/utilisateur/change-password", {
        oldPassword: currentPassword,
        newPassword,
      });
      setSuccessMessage("Mot de passe mis à jour avec succès.");
      setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
      });

      // Rafraîchir la session en mémoire (utile si le backend met à jour dateExpiration, etc.)
      const identifier = getSessionIdentifier();
      if (identifier) {
        axiosInstance
          .get(`/api/utilisateur/session/${identifier}`)
          .then(({ data }) => {
            if (data) persistUser(data);
          })
          .catch((e) => console.error("Erreur lors du rafraîchissement session:", e));
      }
    } catch (err) {
      console.error("Erreur lors du changement de mot de passe :", err);
      if (err?.response?.status === 401) {
        setPasswordError("Ancien mot de passe incorrect ou session expirée.");
      } else if (err?.response?.status === 404) {
        setPasswordError("Utilisateur introuvable.");
      } else {
        setPasswordError("Impossible de modifier le mot de passe pour le moment.");
      }
    } finally {
      setIsChangingPassword(false);
    }
  };
  // --- Fin gestion du formulaire de changement de mot de passe ---


  // --- NOUVELLE FONCTION POUR BASCULER LA VISIBILITÉ ---
  const togglePasswordVisibility = (field) => {
    switch (field) {
      case "current":
        setShowCurrentPassword(prev => !prev);
        break;
      case "new":
        setShowNewPassword(prev => !prev);
        break;
      case "confirm":
        setShowConfirmPassword(prev => !prev);
        break;
      default:
        break;
    }
  };
  // ----------------------------------------------------


  // --- Gestion de l'OTP ---
  const enableOTP = useCallback(() => {
    setErrorMessage("");
    setQrCodeImage("");
    setIsGeneratingQR(true);

    axiosInstance
      .get(`/api/2fa/enable/${LOGGED_USER?.username}`)
      .then((res) => {
        setQrCodeImage(res.data);
        setIsGeneratingQR(false);
      })
      .catch((err) => {
        console.error(err);
        setErrorMessage(
          "Erreur lors de la génération du QR Code. Veuillez réessayer."
        );
        setIsGeneratingQR(false);
      });
  }, []);

  const disableOTP = () => {
    console.log("Désactivation de l'OTP pour:", LOGGED_USER?.username);
    // Logique pour désactiver
  }

  // Vérifie le code à 6 chiffres et confirme l'activation de la 2FA.
  const verifyOTP = (e) => {
    if (e) e.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    const code = (otpCode || "").trim();
    if (!/^\d{6}$/.test(code)) {
      setErrorMessage("Veuillez saisir le code à 6 chiffres généré par votre application Authenticator.");
      return;
    }
    setIsVerifying(true);
    axiosInstance
      .post("/api/2fa/verify", {
        codeId: LOGGED_USER?.username,
        confirmCode: Number(code),
      })
      .then((res) => {
        setIsVerifying(false);
        if (res.data === true) {
          setIsOtpEnabled(true);
          setSuccessMessage("La double authentification (2FA) a été activée avec succès.");
          setOtpCode("");
          const modalEl = document.getElementById("enableOTP");
          if (modalEl && window.bootstrap?.Modal) {
            window.bootstrap.Modal.getOrCreateInstance(modalEl).hide();
          }
        } else {
          setErrorMessage("Code incorrect. Vérifiez le code affiché dans votre application et réessayez.");
        }
      })
      .catch((err) => {
        console.error(err);
        setIsVerifying(false);
        setErrorMessage("Erreur lors de la vérification du code. Veuillez réessayer.");
      });
  };
  // --- Fin gestion de l'OTP ---

  return (
    <div
      className="layout-wrapper layout-content-navbar"
      style={{ background: brand.soft }}
    >
      <div className="layout-container">
        <SideBar />

        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <h4 className="fw-bold py-3 mb-4" style={{ color: brand.primary }}>
                <span className="text-muted fw-light">Mon Profil /</span>{" "}
                Sécurité
              </h4>

              <div className="row">
                <div className="col-md-12">
                  <div className="nav-align-top">
                    <ul
                      className="nav nav-pills flex-column flex-md-row mb-6"
                      style={{
                        background: "#fff",
                        padding: "10px",
                        borderRadius: "14px",
                        border: `1px solid ${brand.border}`,
                        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
                      }}
                    >
                      <li className="nav-item">
                        <a
                          className="nav-link"
                          href="/mon-profil"
                          style={navLinkStyle(false)}
                        >
                          <i className="bx bx-sm bx-user me-1_5"></i> Compte
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link active"
                          href="/mon-profil-securite"
                          style={navLinkStyle(true)}
                        >
                          <i className="bx bx-sm bx-lock-alt me-1_5"></i>{" "}
                          Sécurité
                        </a>
                      </li>
                      <li className="nav-item">
                        <a
                          className="nav-link"
                          href="/mon-profil-notifications"
                          style={navLinkStyle(false)}
                        >
                          <i className="bx bx-sm bx-bell me-1_5"></i>{" "}
                          Notifications
                        </a>
                      </li>
                    </ul>
                  </div>

                  {/* */}
                  <div className="card mb-6" style={cardStyle}>
                    <h5 className="card-header" style={cardHeaderStyle}>
                      Changer de mot de passe
                    </h5>
                    <div className="card-body pt-1">
                      {passwordError && (
                        <div className="alert alert-danger" role="alert">
                          {passwordError}
                        </div>
                      )}
                      {successMessage && (
                        <div className="alert alert-success" role="alert">
                          {successMessage}
                        </div>
                      )}

                      <form
                        id="formAccountSettings"
                        onSubmit={handleChangePassword}
                      >
                        <div className="row">
                          <div className="mb-6 col-md-6 form-password-toggle">
                            <label
                              className="form-label"
                              htmlFor="currentPassword"
                            >
                              Mot de passe actuel
                            </label>
                            <div className="input-group input-group-merge">
                              <input
                                className="form-control"
                                type={showCurrentPassword ? "text" : "password"} // Type dynamique
                                name="currentPassword"
                                id="currentPassword"
                                value={passwordForm.currentPassword}
                                onChange={handleChange}
                                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                                required
                              />
                              <span 
                                className="input-group-text cursor-pointer"
                                onClick={() => togglePasswordVisibility("current")} // Rendre cliquable
                              >
                                <i className={`bx ${showCurrentPassword ? 'bx-show' : 'bx-hide'}`}></i> {/* Icône dynamique */}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="row">
                          <div className="mb-6 col-md-6 form-password-toggle">
                            <label className="form-label" htmlFor="newPassword">
                              Nouveau mot de passe
                            </label>
                            <div className="input-group input-group-merge">
                              <input
                                className="form-control"
                                type={showNewPassword ? "text" : "password"} // Type dynamique
                                id="newPassword"
                                name="newPassword"
                                value={passwordForm.newPassword}
                                onChange={handleChange}
                                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                                required
                              />
                              <span 
                                className="input-group-text cursor-pointer"
                                onClick={() => togglePasswordVisibility("new")} // Rendre cliquable
                              >
                                <i className={`bx ${showNewPassword ? 'bx-show' : 'bx-hide'}`}></i> {/* Icône dynamique */}
                              </span>
                            </div>
                          </div>

                          <div className="mb-6 col-md-6 form-password-toggle">
                            <label
                              className="form-label"
                              htmlFor="confirmPassword"
                            >
                              Confirmer le nouveau mot de passe
                            </label>
                            <div className="input-group input-group-merge">
                              <input
                                className="form-control"
                                type={showConfirmPassword ? "text" : "password"} // Type dynamique
                                name="confirmPassword"
                                id="confirmPassword"
                                value={passwordForm.confirmPassword}
                                onChange={handleChange}
                                placeholder="&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;&#xb7;"
                                required
                              />
                              <span 
                                className="input-group-text cursor-pointer"
                                onClick={() => togglePasswordVisibility("confirm")} // Rendre cliquable
                              >
                                <i className={`bx ${showConfirmPassword ? 'bx-show' : 'bx-hide'}`}></i> {/* Icône dynamique */}
                              </span>
                            </div>
                          </div>
                        </div>
                        <h6 className="text-body">
                          Exigences relatives au mot de passe:
                        </h6>
                        <ul className="ps-4 mb-0">
                          <li className="mb-4">
                            Minimum de **8 caractères** de long.
                          </li>
                          <li className="mb-4">
                            Au moins **un caractère minuscule**.
                          </li>
                          <li>
                            Au moins **un chiffre**, un symbole ou un caractère
                            d'espacement.
                          </li>
                        </ul>
                        <div className="mt-6">
                          <button
                            type="submit"
                            className="btn btn-primary me-3"
                            disabled={isChangingPassword}
                            style={primaryButtonStyle}
                          >
                            {isChangingPassword ? "Modification..." : "Sauvegarder les changements"}
                          </button>
                          {/* <button
                            type="reset"
                            className="btn btn-label-secondary"
                            style={ghostButtonStyle}
                            onClick={() =>
                              setPasswordForm({
                                currentPassword: "",
                                newPassword: "",
                                confirmPassword: "",
                              })
                            }
                          >
                            Réinitialiser
                          </button> */}
                        </div>
                      </form>
                    </div>
                  </div>
                  {/* */}

                  {/* */}
                  <div className="card mb-6" style={cardStyle}>
                    <div className="card-body">
                      <h5 className="mb-2">Vérification en deux étapes (2FA)</h5>
                      <div className="d-flex justify-content-between align-items-center flex-wrap">
                        <div>
                          <p className="mb-4 text-body">
                            L'authentification à deux facteurs ajoute une couche
                            de sécurité supplémentaire à votre compte en
                            exigeant plus qu'un simple mot de passe.
                          </p>
                          <h6 className={`mb-2 ${isOtpEnabled ? 'text-success' : 'text-danger'}`}>
                            Statut actuel : {isOtpEnabled ? 'Activée' : 'Désactivée'}
                          </h6>
                          <a href="javascript:void(0);" className="text-nowrap">
                            Apprendre encore plus sur la 2FA.
                          </a>
                        </div>
                        <div className="mt-3 mt-md-0">
                          {isOtpEnabled ? (
                            <button
                                className="btn btn-outline-danger"
                                onClick={disableOTP}
                            >
                              Désactiver la 2FA
                            </button>
                          ) : (
                            <button
                              className="btn btn-primary"
                              data-bs-toggle="modal"
                              data-bs-target="#enableOTP"
                              onClick={enableOTP}
                              style={primaryButtonStyle}
                            >
                              Activer Google Authenticator
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* */}

                  {/* Sécurité par appareil */}
                  <MesAppareils />

                  {/* */}
                  {/* */}
                  <div
                    className="modal fade"
                    id="enableOTP"
                    tabIndex="-1"
                    aria-hidden="true"
                  >
                    <div className="modal-dialog modal-simple modal-enable-otp modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-body">
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></button>
                          <div className="text-center mb-6">
                            <h4 className="mb-2">
                              Activer la 2FA avec Google Authenticator
                            </h4>
                            <p>
                              Utiliser l'application Google Authenticator pour
                              scanner le code.
                            </p>
                          </div>
                          <p className="text-center">
                            **Étape 1 :** Scannez le QR Code ci-dessous avec
                            votre application Authenticator.
                          </p>

                          <div className="text-center my-4">
                            {isGeneratingQR ? (
                              <div className="spinner-border" role="status">
                                <span className="visually-hidden">
                                  Génération du QR Code...
                                </span>
                              </div>
                            ) : qrCodeImage ? (
                              <QRCode value={qrCodeImage} size={180} />
                            ) : (
                              errorMessage && (
                                <p className="text-danger">{errorMessage}</p>
                              )
                            )}
                          </div>
                          <p className="text-center">
                            **Étape 2 :** Entrez le code à 6 chiffres généré
                            pour confirmer l'activation.
                          </p>
                          <form onSubmit={verifyOTP}>
                            <div className="mb-6">
                                <label htmlFor="otpCode" className="form-label">Code de Vérification</label>
                                <input
                                    type="text"
                                    id="otpCode"
                                    className="form-control"
                                    placeholder="XXXXXX"
                                    maxLength="6"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                    required
                                />
                            </div>
                            {errorMessage && (
                              <div className="alert alert-danger py-2" role="alert">
                                {errorMessage}
                              </div>
                            )}
                            <button
                              type="submit"
                              className="btn btn-primary w-100"
                              style={primaryButtonStyle}
                              disabled={isVerifying || otpCode.length !== 6}
                            >
                                {isVerifying ? "Vérification…" : "Valider et Activer"}
                            </button>
                          </form>
                          
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* */}
                </div>
              </div>
            </div>
            {/* */}

            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default ProfileSecurity;
